import { Alert, LinearProgress } from "@mui/material";
import type { FC } from "react";
import { useParams, useSearchParams } from "react-router";
import { MomentForm } from "../../modules/moment/component/moment-form";
import { RoundaboutContextProvider } from "../../modules/moment/component/roundabout/roundabout-context";
import { useConnections } from "../../modules/moment/hooks/use-connections";
import { useMoment } from "../../modules/moment/hooks/use-moment";
import { useMomentRestrictions } from "../../modules/moment/hooks/use-moment-restrictions";
import type { UUID } from "../../utils/uuid";

const MomentFormPage: FC<{ isNewsletter?: boolean }> = ({ isNewsletter }) => {
  return <MomentFormPageMomentLoad isNewsletter={isNewsletter} />;
};

const MomentFormPageMomentLoad: FC<{
  isNewsletter?: boolean;
}> = ({ isNewsletter }) => {
  const { id } = useParams<{ id: UUID }>();
  const [searchParams] = useSearchParams();
  const {
    data: connectionCategories,
    loading: connectionCategoriesLoading,
    error: connectionCategoriesError,
  } = useConnections();

  const {
    data: momentRestrictions,
    loading: momentRestrictionsLoading,
    error: momentRestrictionsError,
  } = useMomentRestrictions();

  const programId = searchParams.get("program_id") as UUID | undefined;

  const { loading, error, data: moment, reload } = useMoment(id, { program_id: programId });

  if (error || connectionCategoriesError || momentRestrictionsError) {
    return (
      <Alert severity="error">
        {error?.message || connectionCategoriesError?.message || momentRestrictionsError?.message}
      </Alert>
    );
  }

  return (
    // The context has to be high enough to have a unbroken render chain to maintain the websocket ref
    // When the moment form reloads, the context is reinitialised, and the websocket is reconnected.
    <RoundaboutContextProvider momentId={id} reloadMoment={reload}>
      {loading || !moment || connectionCategoriesLoading || momentRestrictionsLoading ? (
        <LinearProgress />
      ) : (
        <MomentForm
          moment={moment}
          reloadMoment={reload}
          isNewsletter={isNewsletter}
          connectionCategories={connectionCategories.list}
          momentRestrictions={momentRestrictions}
        />
      )}
    </RoundaboutContextProvider>
  );
};

export default MomentFormPage;
